import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import { PostCard } from "../components/DiscussionBoard";

/* Permanent public page for a single community post: /community/post/:pid */
export default function CommunityPost() {
  const { pid } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);
  const [followed, setFollowed] = useState(() => new Set());

  const load = useCallback(() => api.get(`/community/posts/${pid}`)
    .then((r) => setPost(r.data)).catch(() => setErr(true)), [pid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!user) return;
    api.get("/community/follow/ids").then((r) => setFollowed(new Set(r.data.ids))).catch(() => {});
  }, [user]);

  const react = async (id, kindR) => {
    if (!user) { toast.error("Sign in to react"); return; }
    try { await api.post(`/community/posts/${id}/react`, { kind: kindR }); load(); } catch { /* noop */ }
  };
  const save = async (id) => {
    if (!user) { toast.error("Sign in to save"); return; }
    try { const r = await api.post(`/community/posts/${id}/save`); toast.success(r.data.saved ? "Saved" : "Removed from saved"); load(); } catch { /* noop */ }
  };
  const report = async (id) => {
    if (!user) { toast.error("Sign in to report"); return; }
    const reason = window.prompt("Why are you reporting this post? (optional)") ?? null;
    if (reason === null) return;
    try { await api.post(`/community/posts/${id}/report`, { reason }); toast.success("Reported"); } catch { toast.error("Could not report"); }
  };
  const del = async (id) => {
    try { await api.delete(`/community/posts/${id}`); toast.success("Post deleted"); setErr(true); } catch { toast.error("Could not delete"); }
  };
  const solve = async (postId, commentId) => {
    try { await api.post(`/community/posts/${postId}/solve`, { comment_id: commentId }); toast.success("Marked as the helpful answer"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not mark answer"); }
  };
  const followUser = async (uid) => {
    if (!user) { toast.error("Sign in to follow travellers"); return; }
    try {
      const r = await api.post(`/community/follow/${uid}`);
      setFollowed((prev) => { const n = new Set(prev); r.data.following ? n.add(uid) : n.delete(uid); return n; });
    } catch { /* noop */ }
  };

  if (err) return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="community-post-notfound">
      <h1 className="font-heading font-900 text-2xl mb-2">This post isn't available</h1>
      <p className="text-sm text-muted-foreground">It may have been removed by its author or our moderators.</p>
      <Link to="/community" className="inline-block mt-4 text-primary hover:underline text-sm">Explore the TravelerPulse Community →</Link>
    </div>
  );
  if (!post) return <Spinner full />;

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid="community-post-page">
      <Link to="/community" className="inline-flex items-center gap-1.5 text-sm font-700 text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="w-4 h-4" /> TravelerPulse Community
      </Link>
      <h1 className="font-heading font-900 text-2xl mb-1 flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-fuchsia-600" /> {post.entity_name || "Community"} Talk – Vibes
      </h1>
      <p className="text-xs text-muted-foreground mb-5">A post from the TravelerPulse community — traveler-shared information.</p>
      <PostCard p={post} user={user} canOfficial={false} followed={followed} onFollow={followUser}
        onReact={react} onSave={save} onReport={report} onDelete={del} onReload={load} onSolve={solve} isStory={post.kind === "story"} />
    </div>
  );
}
